// Structured product-category templates for physical goods (artist + corporate stores).
// Each subcategory defines the fields the seller sees when uploading.
// Field types: "text", "textarea", "number", "select", "multi-select", "date", "toggle", "chips".

export const SUBCATEGORY_LABELS = {
  clothing: "Clothing",
  caps: "Caps",
  bags: "Bags / Purses",
  shoes: "Shoes",
  beauty: "Beauty Products",
  gadgets: "Gadgets",
  dolls: "Stuffed Dolls / Animals",
  jewelry: "Jewelry",
  accessories: "Accessories",
  food: "Food",
  vehicles: "Transportation Vehicles",
};

// Reusable option arrays
const SIZE_APPAREL = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];
const SIZE_SHOE_US_MEN = ["6", "7", "8", "9", "10", "11", "12", "13", "14"];
const SIZE_SHOE_US_WOMEN = ["5", "6", "7", "8", "9", "10", "11", "12"];
const GENDER = ["Unisex", "Men", "Women", "Kids"];
const CONDITION = ["Brand New", "Like New", "Used - Excellent", "Used - Good", "Used - Fair"];
const FUEL_TYPES = ["Petrol", "Diesel", "Electric", "Hybrid", "LPG/CNG", "Jet Fuel", "Marine Diesel", "Manual (bicycle-style)"];
const VEHICLE_TYPE = ["Car", "Motorbike", "Boat", "Plane", "Truck", "Bicycle", "Other"];
const TRANSMISSION = ["Manual", "Automatic", "CVT", "N/A"];
const CLOSURE = ["Zipper", "Magnetic", "Button", "Buckle", "Drawstring", "Open Top", "Snap"];

export const SUBCATEGORY_TEMPLATES = {
  clothing: [
    { key: "sizes", label: "Available Sizes", type: "chips", options: SIZE_APPAREL },
    { key: "material", label: "Material", type: "text", placeholder: "e.g. 100% cotton" },
    { key: "colors", label: "Colors", type: "chips-free", placeholder: "Add a color" },
    { key: "gender", label: "Gender", type: "select", options: GENDER },
    { key: "care_instructions", label: "Care instructions", type: "textarea", placeholder: "e.g. Machine wash cold, tumble dry low" },
    { key: "brand", label: "Brand (optional)", type: "text" },
  ],

  caps: [
    { key: "sizes", label: "Sizes", type: "chips", options: ["One Size", "S/M", "L/XL", "Youth"] },
    { key: "material", label: "Material", type: "text", placeholder: "e.g. cotton twill" },
    { key: "adjustable", label: "Adjustable?", type: "toggle" },
    { key: "colors", label: "Colors", type: "chips-free" },
  ],

  bags: [
    { key: "width_cm", label: "Width (cm)", type: "number" },
    { key: "height_cm", label: "Height (cm)", type: "number" },
    { key: "depth_cm", label: "Depth (cm)", type: "number" },
    { key: "material", label: "Material", type: "text", placeholder: "e.g. genuine leather" },
    { key: "capacity", label: "Interior capacity", type: "text", placeholder: "e.g. holds 13\" laptop, wallet, phone" },
    { key: "closure", label: "Closure", type: "select", options: CLOSURE },
    { key: "colors", label: "Colors", type: "chips-free" },
  ],

  shoes: [
    { key: "size_system", label: "Size system", type: "select", options: ["US", "EU", "UK"] },
    { key: "sizes_men", label: "Men's sizes", type: "chips", options: SIZE_SHOE_US_MEN },
    { key: "sizes_women", label: "Women's sizes", type: "chips", options: SIZE_SHOE_US_WOMEN },
    { key: "material", label: "Material", type: "text", placeholder: "e.g. leather upper, rubber sole" },
    { key: "style", label: "Style", type: "text", placeholder: "e.g. Sneaker / Boot / Sandal" },
    { key: "colors", label: "Colors", type: "chips-free" },
    { key: "gender", label: "Gender", type: "select", options: GENDER },
  ],

  beauty: [
    { key: "ingredients", label: "Ingredients (INCI list)", type: "textarea", placeholder: "Aqua, Glycerin, …" },
    { key: "volume_ml", label: "Volume / Weight", type: "text", placeholder: "e.g. 50ml or 30g" },
    { key: "skin_type", label: "Suitable skin type", type: "multi-select", options: ["All", "Oily", "Dry", "Combination", "Sensitive", "Mature"] },
    { key: "expiry_date", label: "Expiry / PAO", type: "text", placeholder: "e.g. 2028-06 or 12M after opening" },
    { key: "cruelty_free", label: "Cruelty-free?", type: "toggle" },
    { key: "vegan", label: "Vegan?", type: "toggle" },
  ],

  gadgets: [
    { key: "brand", label: "Brand", type: "text" },
    { key: "model", label: "Model", type: "text" },
    { key: "condition", label: "Condition", type: "select", options: CONDITION },
    { key: "tech_specs", label: "Tech specifications", type: "textarea", placeholder: "e.g. 8GB RAM, 256GB SSD, Bluetooth 5.3…" },
    { key: "warranty_months", label: "Warranty (months)", type: "number" },
    { key: "power", label: "Power / Battery", type: "text", placeholder: "e.g. USB-C 65W, 5000mAh" },
    { key: "boxed", label: "Original box included?", type: "toggle" },
  ],

  dolls: [
    { key: "size_cm", label: "Size (cm)", type: "number" },
    { key: "material", label: "Material", type: "select", options: ["Plush", "Wood", "Plastic", "Fabric", "Mixed"] },
    { key: "age_recommendation", label: "Age recommendation", type: "text", placeholder: "e.g. 3+ years" },
    { key: "safety_certification", label: "Safety certification", type: "text", placeholder: "e.g. CE, EN71, ASTM F963" },
    { key: "washable", label: "Machine-washable?", type: "toggle" },
  ],

  jewelry: [
    { key: "metal", label: "Metal", type: "select", options: ["18k Gold", "14k Gold", "9k Gold", "Rose Gold", "Sterling Silver", "Platinum", "Stainless Steel", "Costume/Plated"] },
    { key: "gemstone", label: "Gemstone (if any)", type: "text", placeholder: "e.g. Natural sapphire 0.5ct" },
    { key: "weight_g", label: "Total weight (g)", type: "number" },
    { key: "ring_size", label: "Ring size / chain length (cm)", type: "text" },
    { key: "hallmark", label: "Hallmark / certification", type: "text", placeholder: "e.g. GIA cert #12345678" },
    { key: "handmade", label: "Handmade?", type: "toggle" },
  ],

  accessories: [
    { key: "accessory_type", label: "Type", type: "select", options: ["Belt", "Watch", "Scarf", "Sunglasses", "Wallet", "Keychain", "Other"] },
    { key: "material", label: "Material", type: "text" },
    { key: "colors", label: "Colors", type: "chips-free" },
    { key: "adjustable", label: "Adjustable / one-size?", type: "toggle" },
  ],

  food: [
    { key: "ingredients", label: "Ingredients", type: "textarea", placeholder: "Ordered by weight" },
    { key: "allergens", label: "Allergens", type: "multi-select", options: ["Gluten", "Dairy", "Nuts", "Peanuts", "Soy", "Eggs", "Fish", "Shellfish", "Sesame", "None"] },
    { key: "weight", label: "Weight", type: "text", placeholder: "e.g. 250g" },
    { key: "expiry_date", label: "Best-before date", type: "date" },
    { key: "storage", label: "Storage instructions", type: "text", placeholder: "e.g. Refrigerate at 2-8°C" },
    { key: "dietary", label: "Dietary", type: "multi-select", options: ["Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-Free", "Organic", "Sugar-Free"] },
  ],

  vehicles: [
    { key: "vehicle_type", label: "Type of vehicle", type: "select", options: VEHICLE_TYPE },
    { key: "make", label: "Make", type: "text", placeholder: "e.g. Tesla" },
    { key: "model", label: "Model", type: "text", placeholder: "e.g. Model 3" },
    { key: "year", label: "Year", type: "number" },
    { key: "vin", label: "VIN / Serial number", type: "text" },
    { key: "mileage_km", label: "Mileage (km) or hours", type: "number" },
    { key: "condition", label: "Condition", type: "select", options: CONDITION },
    { key: "fuel_type", label: "Fuel / power source", type: "select", options: FUEL_TYPES },
    { key: "transmission", label: "Transmission", type: "select", options: TRANSMISSION },
    { key: "registered", label: "Currently registered?", type: "toggle" },
    { key: "notes", label: "Ownership / history notes", type: "textarea" },
  ],
};

export function isPhysicalSubcategory(subcategory) {
  return !!SUBCATEGORY_TEMPLATES[subcategory];
}

export function getSubcategoryFields(subcategory) {
  return SUBCATEGORY_TEMPLATES[subcategory] || [];
}
